import { ButtonLink } from '@domgen/base-design-system-2.0/atoms/ButtonLink';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { gaECommerceEvent, gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { usePostHog } from '@posthog/react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy } from '../../assets/icons/Copy';
import { StoreEvent } from '../../enums/storeEvent';
import { QuoteResponse } from '../../models/services/api/postQuote';
import { capturePostHogEvent } from '../../services/posthog/capture';
import { useAppState } from '../../store/selectors/useAppState';
import { useConfirmation } from '../../store/selectors/useConfirmation';
import { useGaEventData } from '../../store/selectors/useGaEventData';
import { useQuote } from '../../store/selectors/useQuote';
import { useUrlParams } from '../../store/selectors/useUrlParams';
import { store } from '../../store/store';
import { getDiscountDisplayData } from '../../utils/discounts/getDiscountDisplayData';
import { getSystemCode } from '../../utils/getSystemCode';
import { getConfirmationEventProperties } from '../../utils/posthog/getConfirmationEventProperties';
import { capitalizeFirstLetter } from '../../utils/string/capitalize';
import { BottomPanel } from './components/BottomPanel';
import { ConfirmationUpsellPanel } from './components/ConfirmationUpsellPanel';
import { ConfirmationListItem } from './ConfirmationPage.types';
import { cpgImages } from './constants/cpgImages';
import { formatPurchasedPlan, formatRegistrationData } from './utils/formatConfirmationPanels';

const styles = {
  container: 'grid grid-cols-12 gap-4 items-center p-space-500 bg-neutral-50',
  panel: 'col-span-12 w-2xl h-auto p-5 rounded-(--border-radius) bg-neutral-0 m-2 self-center',
  descriptionList: 'flex flex-col gap-2 mt-4 w-full',
  descriptionListItemTitle: 'text-body-small-strong mb-space-100',
  descriptionListItem:
    'w-full text-body-small inline-flex justify-start items-center gap-1 mb-2 bg-bg-main border border-inset border-card-border-neutral rounded-radius-100 px-space-200 py-space-100',
  article: 'col-span-6 rounded-radius-200 bg-neutral-0 p-space-500 w-full h-fit',
  cpgPanel: 'flex flex-col justify-center items-center gap-2 text-center',
};

export function ConfirmationPage() {
  const { t } = useTranslation('confirmationPage');
  const {
    VITE_CLIENT_FRIENDLY_NAME,
    VITE_APPLIANCE_CARE_TERMS_AND_CONDITIONS_PATH,
    VITE_BRAND_CODE,
    VITE_PRODUCT_HELP_TEMPLATE_PATH,
  } = import.meta.env;
  const confirmation = useConfirmation();
  const { cpgData, regOnlyData } = confirmation ?? {};
  const { session } = useAppState();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const [isCopied, setIsCopied] = useState(false);
  const posthog = usePostHog();
  const registerOnly =
    regOnlyData && regOnlyData.items?.length > 0 ? regOnlyData.items[0] : undefined;
  const quote = useQuote()?.data as QuoteResponse | undefined;
  const discountTerms = quote && quote?.offer ? getDiscountDisplayData(quote) : undefined;

  const { applianceName, categoryName, modelNumber, serialNumber, arrivalDate, purchasePrice } =
    confirmation?.applianceDetails ?? {};

  useEffect(() => {
    if (confirmation?.regOnlyData) {
      capturePostHogEvent(posthog, 'registration-only-order-confirmation-loaded', store.get());
    } else {
      capturePostHogEvent(
        posthog,
        'registration-upsell-order-confirmation-loaded',
        store.get(),
        getConfirmationEventProperties(store.get().context),
      );
    }
  }, []);

  const { discountCode, startDate, netFirstPaymentAmount, itemId } = confirmation?.offerData ?? {};
  const gaEventData = useGaEventData() || {};
  const {
    experimentName,
    experimentVariant,
    checkoutExperimentName,
    checkoutExperimentVariant,
    salesWidgetClicked,
  } = gaEventData;

  const {
    discountTermFromPurchase,
    discountCodeFromPurchase,
    mappedPaymentType,
    transactionId,
    planNumber,
    netFirstPaymentAmountFromPurchase,
    netFirstPaymentAmountAfterPromotionalPeriodFromPurchase,
  } = confirmation?.purchaseData ?? {};

  const appSessionID = session?.id;

  const formattedDeliveryDate = arrivalDate
    ? formatLocalisedDate(parseLocalDateString(arrivalDate), 'en-US')
    : '';
  const formattedStartDate = startDate
    ? formatLocalisedDate(parseLocalDateString(startDate), 'en-US')
    : '';

  const registration = formatRegistrationData(
    VITE_CLIENT_FRIENDLY_NAME,
    applianceName,
    modelNumber,
    serialNumber,
    formattedDeliveryDate,
    {
      panelTitle: t('registration.panelTitle'),
      appliance: t('registration.applianceLabel'),
      modelNumber: t('registration.modelNumberLabel'),
      serialNumber: t('registration.serialNumberLabel'),
      purchaseDate: t('registration.purchaseDateLabel'),
    },
  );

  const purchasePlan = confirmation?.purchaseData
    ? formatPurchasedPlan(
        VITE_CLIENT_FRIENDLY_NAME,
        applianceName,
        formattedStartDate,
        planNumber,
        netFirstPaymentAmountFromPurchase,
        netFirstPaymentAmountAfterPromotionalPeriodFromPurchase,
        discountTermFromPurchase,
        {
          panelTitle: t('plan.panelTitle'),
          appliance: t('plan.applianceLabel'),
          planNumber: t('plan.planNumberLabel'),
          planStartDate: t('plan.planStartDateLabel'),
          monthlyPayment: t('plan.monthlyPaymentLabel'),
          monthlyPaymentFirstMonths: t('plan.monthlyPaymentFirstMonthsLabel', {
            months: discountTermFromPurchase,
          }),
          monthlyPaymentAfterMonths: t('plan.monthlyPaymentAfterMonthsLabel', {
            months: discountTermFromPurchase,
          }),
        },
      )
    : null;

  const offerStatus = confirmation?.offerData ? '1' : '0';

  useEffect(() => {
    if (confirmation?.regOnlyData) registerOnlyGaEvents();
    else purchasePriceGaEvents();
  }, []);

  const registerOnlyGaEvents = () => {
    gaPageView({
      event: 'spa-pageview',
      pagename: 'confirmation',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory: categoryName,
      offerPrice: netFirstPaymentAmount,
      appliancePurchaseCost: purchasePrice,
      appliancePurchaseDate: formattedDeliveryDate,
      modelNumberDetail: modelNumber,
      serialNumberDetail: serialNumber,
      serialNumber: serialNumber ? 'provided' : 'not provided',
      modelNumber: modelNumber ? 'provided' : 'not provided',
      promotionApplied: discountCodeFromPurchase ?? discountCode,
      offerStatus,
      salesWidgetClicked,
      ...(confirmation?.offerData?.offerReference && {
        quoteId: confirmation.offerData?.offerReference,
      }),
      experimentName: offerStatus === '1' ? experimentName : undefined,
      experimentVariant: offerStatus === '1' ? experimentVariant : undefined,
    });
    gaEvent({
      event: 'registrationStored',
      eventLabel: '1',
      pagename: 'confirmation',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory: categoryName,
      offerPrice: netFirstPaymentAmount,
      appliancePurchaseCost: purchasePrice,
      appliancePurchaseDate: formattedDeliveryDate,
      modelNumberDetail: modelNumber,
      serialNumberDetail: serialNumber,
      serialNumber: serialNumber ? 'provided' : 'not provided',
      modelNumber: modelNumber ? 'provided' : 'not provided',
      promotionApplied: discountCodeFromPurchase ?? discountCode,
      registrationStored: '1',
      offerStatus,
      salesWidgetClicked,
      ...(confirmation?.offerData?.offerReference && {
        quoteId: confirmation.offerData?.offerReference,
      }),
      experimentName: offerStatus === '1' ? experimentName : undefined,
      experimentVariant: offerStatus === '1' ? experimentVariant : undefined,
    });

    if (discountTerms && registerOnly) {
      gaEvent({
        eventCategory: 'appliance details form USA',
        eventAction: 'Reg Confirmation Sales Widget',
        eventLabel: 'Offer Presented',
        pagename: 'confirmation',
        category: 'registration-pages',
        type: 'STANDARD',
        regSystemCode: systemCode,
        applianceName,
        applianceCategory: categoryName,
        appliancePurchaseCost: purchasePrice,
        appliancePurchaseDate: formattedDeliveryDate,
        modelNumberDetail: modelNumber,
        serialNumberDetail: serialNumber,
        serialNumber: serialNumber ? 'provided' : 'not provided',
        modelNumber: modelNumber ? 'provided' : 'not provided',
        offerPrice: netFirstPaymentAmount,
        appSessionID: session?.id,
        promotionApplied: discountCodeFromPurchase ?? discountCode,
        quoteId: confirmation?.offerData?.offerReference,
        experimentName,
        experimentVariant,
      });
    }
  };

  const purchasePriceGaEvents = () => {
    gaPageView({
      event: 'spa-pageview',
      pagename: 'order-confirmation',
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory: categoryName,
      offerPrice: netFirstPaymentAmount,
      appliancePurchaseCost: purchasePrice,
      appliancePurchaseDate: formattedDeliveryDate,
      modelNumberDetail: modelNumber,
      serialNumberDetail: serialNumber,
      serialNumber: serialNumber ? 'provided' : 'not provided',
      modelNumber: modelNumber ? 'provided' : 'not provided',
      promotionApplied: discountCodeFromPurchase ?? discountCode,
      paymentType: mappedPaymentType === 'cc' ? 'credit card' : 'bank account',
      planNumber,
      salesWidgetClicked,
      quoteId: confirmation?.offerData?.offerReference,
      experimentName,
      experimentVariant,
      checkoutExperimentName,
      checkoutExperimentVariant,
    });

    gaECommerceEvent({
      pagename: 'order-confirmation',
      category: 'registration-pages',
      type: 'STANDARD',
      event: 'purchase',
      regSystemCode: systemCode,
      version: 'WiB',
      domainId: VITE_CLIENT_FRIENDLY_NAME.replaceAll(' ', '').toUpperCase(),
      brand: VITE_BRAND_CODE.trim(),
      country: 'USA',
      customerType: 'G',
      offerStatus,
      item_promotion_name: discountCodeFromPurchase ?? discountCode,
      appSessionID,
      applianceName,
      promotionApplied: discountCodeFromPurchase ?? discountCode,
      applianceCategory: categoryName,
      salesWidgetClicked,
      quoteId: confirmation?.offerData?.offerReference,
      experimentName,
      experimentVariant,
      checkoutExperimentName,
      checkoutExperimentVariant,
      paymentType: mappedPaymentType === 'cc' ? 'credit card' : 'bank account',
      ecommerce: {
        currency: 'USD',
        value: netFirstPaymentAmountFromPurchase,
        transaction_id: transactionId,
        items: [
          {
            item_appliance_purchase_cost: Number(purchasePrice),
            item_appliance_purchase_date: formattedDeliveryDate, // USA format date
            item_appliance_name: applianceName ?? '',
            item_appliance_warranty: true,
            item_brand: VITE_CLIENT_FRIENDLY_NAME,
            item_category: categoryName ?? '',
            item_id: itemId ?? '',
            item_name: `${VITE_CLIENT_FRIENDLY_NAME} ${applianceName}`,
            item_promotion_name: discountCodeFromPurchase ?? discountCode ?? '',
            item_model_number_detail: modelNumber ?? '',
            item_payment_type: mappedPaymentType === 'cc' ? 'credit card' : 'bank account',
            price: netFirstPaymentAmountFromPurchase,
            quantity: 1,
            item_reg_system_code: systemCode,
            item_serial_number_detail: serialNumber ?? '',
          },
        ],
      },
    });
  };

  const header = () =>
    registerOnly
      ? {
          title: t('heading'),
          body: t('registrationConfirmed'),
        }
      : {
          title: t('heading'),
          body: t('registrationAndProtectionConfirmed'),
        };

  const copy = () => {
    if (cpgData) {
      navigator.clipboard.writeText(cpgData.promoCode);
      setIsCopied(true);
    }
  };

  const cpgImage = () => {
    if (!cpgData) {
      return;
    }
    return cpgImages.find((image: Record<string, string>) => image.id === cpgData.filter);
  };

  const registerAnotherApplianceOnClick = useCallback(() => {
    capturePostHogEvent(posthog, 'register-another-appliance-clicked', store.get());

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'CTA clicked',
      eventLabel: 'Register another appliance',
      pagename: registerOnly ? 'confirmation' : 'order-confirmation',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      customerType: 'G',
      domainId: VITE_CLIENT_FRIENDLY_NAME.replaceAll(' ', '').toUpperCase(),
      brand: VITE_BRAND_CODE.trim(),
      appSessionID: session?.id,
      version: 'WiB',
      country: 'USA',
      ...(confirmation?.offerData?.offerReference && {
        quoteId: confirmation.offerData.offerReference,
      }),
    });

    store.send({ type: StoreEvent.RESTART_JOURNEY });
  }, []);

  const productDetailsLink =
    !VITE_PRODUCT_HELP_TEMPLATE_PATH || !modelNumber
      ? undefined
      : VITE_PRODUCT_HELP_TEMPLATE_PATH.replace('{{model_number}}', modelNumber);

  const accessProductDetailsOnClick = useCallback(() => {
    if (!productDetailsLink) {
      return;
    }

    capturePostHogEvent(posthog, 'product-details-cta-clicked', store.get());
    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'CTA clicked',
      eventLabel: 'Access product details',
      pagename: registerOnly ? 'confirmation' : 'order-confirmation',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory: categoryName,
      modelNumberDetail: modelNumber,
      modelNumber: modelNumber ? 'provided' : 'not provided',
      offerPrice: netFirstPaymentAmount,
      ...(confirmation?.offerData?.offerReference && {
        quoteId: confirmation.offerData?.offerReference,
      }),
    });

    window.open(productDetailsLink, '_blank');
  }, []);

  const applianceCare = (
    <div className="flex flex-col text-left">
      <p className="my-4">{t('applianceCareTerms')}</p>
      <TextLink
        href={VITE_APPLIANCE_CARE_TERMS_AND_CONDITIONS_PATH}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('applianceCareTermsLink')}
      </TextLink>
    </div>
  );

  const listItem = (listItem: ConfirmationListItem) => (
    <div key={`${listItem.label}-${listItem.value}`}>
      <dt className={styles.descriptionListItemTitle}>{capitalizeFirstLetter(listItem.label)}</dt>
      <dd className={styles.descriptionListItem}>
        <span className="mr-2 text-(--color-icon-list-decorative)">
          {typeof listItem.icon === 'string' ? (
            <img src={listItem.icon} alt={listItem.label} height={16} width={16} />
          ) : (
            listItem.icon
          )}
        </span>
        {listItem.value}
      </dd>
    </div>
  );

  const upsellBanner = () => (
    <div
      role="marquee"
      className="bg-button-bg-primary-default  text-button-text-primary-default w-full text-center py-space-200 px-space-600"
    >
      <p className="text-body-large-strong">{t('upsellBanner')}</p>
    </div>
  );

  const handleCheckoutNavigation = useCallback(() => {
    capturePostHogEvent(posthog, 'confirmation-upsell-clicked', store.get());

    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'Reg Confirmation Sales Widget ',
      eventLabel: 'CTA Clicked',
      pagename: 'confirmation',
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory: categoryName,
      appliancePurchaseCost: purchasePrice,
      appliancePurchaseDate: formattedDeliveryDate,
      modelNumberDetail: modelNumber,
      serialNumberDetail: serialNumber,
      serialNumber: serialNumber ? 'provided' : 'not provided',
      modelNumber: modelNumber ? 'provided' : 'not provided',
      offerPrice: netFirstPaymentAmount,
      appSessionID: session?.id,
      promotionApplied: discountCodeFromPurchase ?? discountCode,
      quoteId: confirmation?.offerData?.offerReference,
      experimentName,
      experimentVariant,
    });

    store.send({
      type: StoreEvent.CONTINUE_TO_CHECKOUT,
      data: { continueToCheckoutFromModal: false },
    });
  }, []);

  return (
    <>
      {discountTerms && registerOnly && upsellBanner()}
      <div className={styles.container}>
        <header className="col-span-12 text-center">
          <h1 className="text-h3 mb-space-500">{header().title}</h1>
          <p className="mb-space-100">{header().body}</p>
        </header>

        <div className="col-span-12 flex justify-center">
          <div className="w-full max-w-178 grid grid-cols-6 gap-space-300">
            {registration && (
              <article
                className={`@container/registration ${!registerOnly || cpgData ? 'md:col-span-3' : 'md:col-span-6'} ${styles.article}`}
              >
                <h2 className="text-body-large-strong">{registration.panelTitle}</h2>
                <dl className={styles.descriptionList}>
                  {registration.data.map((item) => listItem(item))}
                </dl>
                <BottomPanel
                  productDetailsLink={productDetailsLink}
                  accessProductDetailsOnClick={accessProductDetailsOnClick}
                  registerAnotherApplianceOnClick={registerAnotherApplianceOnClick}
                />
              </article>
            )}

            {purchasePlan && !registerOnly && (
              <article className={`${styles.article} md:col-span-3`}>
                <h2 className="text-body-large-strong">{purchasePlan.panelTitle}</h2>
                <dl className={styles.descriptionList}>
                  {purchasePlan.data.map((item) => listItem(item))}
                </dl>
                {applianceCare}
              </article>
            )}
            {cpgData && cpgImage() && (
              <article
                className={`${!registerOnly && purchasePlan ? 'md:col-start-2' : registerOnly ? 'md:col-start-4' : ''} md:col-span-3 ${styles.article} ${styles.cpgPanel}`}
              >
                <h6 className="text-h6 leading-space-700">{cpgData.title}</h6>
                <img
                  className="max-w-43.25 max-h-43.25"
                  src={`${cpgImage()?.image}`}
                  alt={cpgData.productTitle}
                />
                <p className="text-body-strong"> {cpgData.productTitle}</p>
                <p>{cpgData.description}</p>
                <TextLink onClick={copy} leftIcon={<Copy />}>
                  {isCopied ? t('copied') : t('copyCode')}
                </TextLink>

                <ButtonLink href={cpgData.waterFilterUrl} variant="primary">
                  <span className="uppercase">{cpgData.cta}</span>
                </ButtonLink>
              </article>
            )}
          </div>
        </div>
      </div>

      <div className="bg-neutral-50 w-full flex justify-center py-space-500">
        {registerOnly && discountTerms && applianceName && (
          <ConfirmationUpsellPanel
            applianceName={applianceName}
            discountPaymentAmount={discountTerms.netFirstPaymentAmount}
            discountPaymentType={discountTerms.discountPaymentType}
            discountTerm={discountTerms.discountTerm}
            discountPercentage={discountTerms.discountPercentage}
            handleCheckoutNavigation={handleCheckoutNavigation}
          />
        )}
      </div>
    </>
  );
}
